import { EntityStatus, EntitySubStatus, PARENT_IDENTITY_STATUS_SETS } from './entity.status';
import { EntityStatusType, Party } from './enum';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { CodeStatus } from '../shared/code';
import { isDeactivationPatch } from './system.status.rules';
import { TaskSystemStatusName } from '../tasks/task.status.policy';

/** The status fields a tenant may edit on a converted set; group and identity have their own path, and the default flag is fixed by the seed. */
export const STATUS_SETTINGS_PATCH_KEYS = ['name', 'sortOrder', 'color', 'reasonRequired', 'selectableBy', 'editableBy', 'active'] as const;

export type StatusSettingsPatch = Partial<Pick<EntityStatus, typeof STATUS_SETTINGS_PATCH_KEYS[number]>>;

export type ReasonSettingsPatch = Partial<Pick<EntitySubStatus, 'name' | 'active' | 'sortOrder'>>;

/** WorkBuddy gray 500 in the light theme, the colour a status added by name only starts with. */
export const DEFAULT_STATUS_COLOR = '#717680';

/** The party lists a status added by name only starts with, mirroring Open and Not Required so it is pickable before anyone configures it. */
export function resolveDefaultStatusParties(statusType: EntityStatusType): Pick<EntityStatus, 'selectableBy' | 'editableBy'> {

    const people = [Party.Office, Party.Field, Party.Contractor];

    return { selectableBy: people, editableBy: statusType === EntityStatusType.Inactive ? [] : people };
}

/**
 * What a tenant-made Item status counts as on the line's CodeStatus mirror; the authorised writes refuse a status without one.
 * Inactive can only be Cancelled, because a cancel always writes Cancelled and the write checks the two agree.
 */
export function resolveTenantStatusLegacyStatus(entityType: ActivityEntityName, statusType: EntityStatusType): CodeStatus | undefined {

    if (entityType !== ActivityEntityName.Item) return undefined;

    return statusType === EntityStatusType.Inactive ? CodeStatus.Cancelled : CodeStatus.Normal;
}

const RESERVED_SYSTEM_NAMES: ReadonlySet<string> = new Set([...Object.values(CodeStatus), ...Object.values(TaskSystemStatusName)].map(name => name.toUpperCase()));

/** Why a tenant status may not take this system name; line import matches pending names case-insensitively, so a pending "Variation" made every imported Variation line pending. */
export function findReservedSystemNameRefusal(systemName: string | null | undefined): string | undefined {

    const key = systemName?.trim();

    return key && RESERVED_SYSTEM_NAMES.has(key.toUpperCase()) ? `${key} is reserved for a system status` : undefined;
}

/** Whether this set is edited as statuses with reasons beneath, rather than as groups with sub-statuses. */
export function isParentIdentityStatusSet(entityType: ActivityEntityName | null | undefined): boolean {

    return PARENT_IDENTITY_STATUS_SETS.includes(entityType);
}

/** The reasons a user may pick; the same-named default is the stamp's filler and never shown. */
export function getVisibleReasons(status: Pick<EntityStatus, 'subStatuses'> | null | undefined): EntitySubStatus[] {

    return status?.subStatuses?.filter(reason => !reason.isDefault) ?? [];
}

/** The status a set's new records are created in; it is permanent, so Settings may neither retire nor move it. */
export function isStartingStatus(status: Pick<EntityStatus, 'entityType' | 'isDefault'> | null | undefined): boolean {

    return isParentIdentityStatusSet(status?.entityType) && status.isDefault === true;
}

/** The values a starting status is pinned to; Settings and the tenant upgrade both read these, so they cannot drift. */
export function resolveStartingStatusLocks(entityType: ActivityEntityName): Partial<Pick<EntityStatus, 'reasonRequired' | 'selectableBy'>> {

    // A party able to select Draft could send a Requested line back into its author's edit window.
    if (entityType === ActivityEntityName.ItemPending) return { reasonRequired: false, selectableBy: [] };

    return { reasonRequired: false };
}

function findStartingStatusLockRefusal(existing: EntityStatus, patch: Partial<EntityStatus>): string | undefined {

    const locks = resolveStartingStatusLocks(existing.entityType);

    // Legacy reopen paths send no reason, so a required reason on a starting status refuses every reopen from the field app, portal and API.
    if ('reasonRequired' in locks && 'reasonRequired' in patch && !!patch.reasonRequired !== locks.reasonRequired) return `${existing.name} is the starting status and cannot require a reason`;

    // Runtime reads a null list as unrestricted, so only an actual empty array keeps the lock.
    if ('selectableBy' in locks && 'selectableBy' in patch && !(Array.isArray(patch.selectableBy) && patch.selectableBy.length === 0)) return `${existing.name} is the starting status and cannot be selected by anyone`;

    return undefined;
}

function hasActiveReason(status: Pick<EntityStatus, 'subStatuses'>): boolean {

    return getVisibleReasons(status).some(reason => reason.active !== false);
}

/** Why a patch may not move this status to another set or change its default flag, or undefined when it may; new records start in whichever default the lookup finds first, so a second one breaks that set. */
export function findStatusSetMoveRefusal(existing: Pick<EntityStatus, 'name' | 'entityType' | 'isDefault'> | null | undefined, patch: Partial<Pick<EntityStatus, 'entityType' | 'isDefault'>>): string | undefined {

    if (!existing) return undefined;

    if ('entityType' in patch && patch.entityType !== existing.entityType) return `${existing.name} cannot move to another status set`;

    // Legacy sets set their default on the sub-status, so the status flag only changes through the seed.
    if ('isDefault' in patch && !!patch.isDefault !== !!existing.isDefault) return `${existing.name} cannot change whether it is the starting status`;

    return undefined;
}

/** Why a settings patch may not apply to this status, or undefined when it may. */
export function findStatusSettingsRefusal(existing: EntityStatus | null | undefined, patch: Partial<EntityStatus>): string | undefined {

    if (!existing) return 'Status was not found';

    // Gates read the group, so flipping it would silently move every record holding this status in or out of a gate.
    if (patch.statusType && patch.statusType !== existing.statusType) return `${existing.name} cannot change group; deactivate it and add a new status instead`;

    // Delete now removes the line, so a reactivated Deleted would stamp lines with a state nothing writes or clears.
    if (patch.active === true && existing.systemName === CodeStatus.Deleted) return 'Deleted is retired: a deleted line is removed, not marked';

    if (isStartingStatus(existing)) {
        if (isDeactivationPatch(patch)) return `${existing.name} is the starting status and cannot be deactivated`;

        const lockRefusal = findStartingStatusLockRefusal(existing, patch);

        if (lockRefusal) return lockRefusal;
    }

    // A required reason with nothing to pick refuses every move into the status.
    if (patch.reasonRequired === true && !hasActiveReason(existing)) return `${existing.name} needs an active reason before reasons can be required`;

    return undefined;
}

/** Why a new reason may not take this name, or undefined when it may. */
export function findReasonNameRefusal(status: Pick<EntityStatus, 'name' | 'subStatuses'>, name: string | null | undefined): string | undefined {

    const candidate = name?.trim().toLowerCase();

    // The hidden default always carries the status name, so matching it would refuse a reason named after its status.
    if (getVisibleReasons(status).some(reason => reason.name?.trim().toLowerCase() === candidate)) return `${status.name} already has a reason named ${name.trim()}`;

    return undefined;
}

// Product decision (5 Oct 2026): a starting status carries no reasons, so Settings may neither add nor edit one; its hidden default stays as the stamp's filler.
function findStartingStatusReasonRefusal(status: EntityStatus): string | undefined {

    return isStartingStatus(status) ? `${status.name} is the starting status and carries no reasons` : undefined;
}

/** Why a reason may not be added to this status under this name, or undefined when it may. */
export function findReasonCreateRefusal(status: EntityStatus | null | undefined, name: string | null | undefined): string | undefined {

    if (!status) return 'Status was not found';

    return findStartingStatusReasonRefusal(status) ?? findReasonNameRefusal(status, name);
}

/** Why a settings patch may not apply to this reason, or undefined when it may. */
export function findReasonSettingsRefusal(status: EntityStatus | null | undefined, reason: EntitySubStatus | null | undefined, patch: Partial<EntitySubStatus>): string | undefined {

    if (!status || !reason) return 'Reason was not found';

    if (reason.isDefault) return 'The default reason cannot be edited';
    if ('isDefault' in patch) return 'A reason cannot be the default; set the default on the status';

    const startingRefusal = findStartingStatusReasonRefusal(status);

    if (startingRefusal) return startingRefusal;

    const others = { name: status.name, subStatuses: status.subStatuses.filter(candidate => candidate._id !== reason._id) };

    if ('name' in patch) {
        const nameRefusal = findReasonNameRefusal(others, patch.name);

        if (nameRefusal) return nameRefusal;
    }

    if (patch.active === false && status.reasonRequired && !hasActiveReason(others)) return `${reason.name} is the last active reason of ${status.name}, which requires one`;

    return undefined;
}

/** A tenant status may be retired; a system status or the set's default may not. */
export function canDeactivateStatus(status: EntityStatus | null | undefined): boolean {

    return !!status?.active && !status.system && !status.isDefault;
}

/** A deactivated status may come back unless it is one the model has retired. */
export function canReactivateStatus(status: EntityStatus | null | undefined): boolean {

    return !!status && !status.active && !findStatusSettingsRefusal(status, { active: true });
}
